import React, { useState, useEffect } from 'react';

const ROLES = { VIEWER: 'viewer', ADMIN: 'admin', SUPERUSER: 'superuser' };

const ViewClient = ({ clientConfig, onLaunchApp, subscriberMode }) => {
  const [clientId, setClientId] = useState(clientConfig?.client_id || '');
  const [clientData, setClientData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(ROLES.VIEWER);
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState(null);

  const canDownloadFiles = userRole === ROLES.ADMIN || userRole === ROLES.SUPERUSER;

  // ── Auto-load subscriber's own client data when clientConfig is provided ──
  useEffect(() => {
    if (subscriberMode && clientConfig) {
      // Only fetch from backend if we have a real provisioned client_id
      // (starts with 'client_' — not a company name or empty string)
      const realClientId = clientConfig.client_id;
      if (realClientId && realClientId.startsWith('client_')) {
        setClientId(realClientId);
        setLoading(true);
        fetch(`/v1/clients/${realClientId}/status`)
          .then(res => res.ok ? res.json() : null)
          .then(liveData => {
            if (liveData) {
              setClientData(liveData);
            } else {
              // Backend unreachable — fall back to clientConfig
              setClientData({
                client_id: clientConfig.client_id || '',
                company_name: clientConfig.company_name || '',
                product_name: clientConfig.product_name || clientConfig.product_type || '',
                product_type: clientConfig.product_type || '',
                plan: clientConfig.plan || 'growth',
                tenant_type: clientConfig.tenant_type || 'INDEPENDENT',
                tenant_role: clientConfig.tenant_role || 'subscriber',
                status: 'active',
                location: clientConfig.location || '',
                contact_email: clientConfig.contact_email || '',
                rag_status: clientConfig.rag_status || 'pending',
                genai_status: clientConfig.genai_status || 'pending',
                agentic_status: clientConfig.agentic_status || 'pending',
                created_at: clientConfig.created_at || '',
                default_features: clientConfig.default_features || ['Comm-AI-Chat', 'LLM-Reporting'],
              });
            }
          })
          .catch(() => {
            // Network error — use clientConfig data
            setClientData({
              client_id: clientConfig.client_id || '',
              company_name: clientConfig.company_name || '',
              product_name: clientConfig.product_name || clientConfig.product_type || '',
              product_type: clientConfig.product_type || '',
              plan: clientConfig.plan || 'growth',
              tenant_type: clientConfig.tenant_type || 'INDEPENDENT',
              tenant_role: clientConfig.tenant_role || 'subscriber',
              status: 'active',
              location: clientConfig.location || '',
              contact_email: clientConfig.contact_email || '',
              rag_status: clientConfig.rag_status || 'pending',
              genai_status: clientConfig.genai_status || 'pending',
              agentic_status: clientConfig.agentic_status || 'pending',
              created_at: clientConfig.created_at || '',
              default_features: clientConfig.default_features || ['Comm-AI-Chat', 'LLM-Reporting'],
            });
          })
          .finally(() => setLoading(false));
      } else {
        // No real client_id — use clientConfig directly
        setClientData({
          client_id: clientConfig.client_id || '',
          company_name: clientConfig.company_name || '',
          product_name: clientConfig.product_name || clientConfig.product_type || '',
          product_type: clientConfig.product_type || '',
          plan: clientConfig.plan || 'growth',
          tenant_type: clientConfig.tenant_type || 'INDEPENDENT',
          tenant_role: clientConfig.tenant_role || 'subscriber',
          status: 'active',
          location: clientConfig.location || '',
          contact_email: clientConfig.contact_email || '',
          rag_status: clientConfig.rag_status || 'pending',
          genai_status: clientConfig.genai_status || 'pending',
          agentic_status: clientConfig.agentic_status || 'pending',
          created_at: clientConfig.created_at || '',
          default_features: clientConfig.default_features || ['Comm-AI-Chat', 'LLM-Reporting'],
        });
      }
    }
  }, [subscriberMode, clientConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLookup = async () => {
    if (!clientId.trim()) { setError('Enter a client ID'); return; }
    setError(null); setClientData(null); setLoading(true);

    // Always try demo data first for known client IDs (works offline)
    const demo = getDemoClient(clientId.trim());

    try {
      const res = await fetch(`/v1/clients/${clientId.trim()}/status`);
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          setClientData(data);
          // Persist to localStorage so ListClients can discover this client
          try {
            const history = JSON.parse(localStorage.getItem('pnp_view_history') || '[]');
            if (!history.includes(clientId.trim())) {
              history.push(clientId.trim());
              localStorage.setItem('pnp_view_history', JSON.stringify(history));
            }
            // Also persist as onboarded client for ListClients
            const existing = JSON.parse(localStorage.getItem('pnp_onboarded_clients') || '[]');
            if (!existing.find(c => c.client_id === data.client_id)) {
              existing.push({
                client_id: data.client_id,
                company_name: data.company_name || '',
                plan: data.plan || 'starter',
                tenant_type: data.tenant_type || 'INDEPENDENT',
                status: data.status || 'active',
                product_name: data.product_name || '',
                product_type: data.product_type || '',
                location: data.location || '',
                app_model: '',
                created_at: data.created_at || new Date().toISOString(),
              });
              localStorage.setItem('pnp_onboarded_clients', JSON.stringify(existing));
            }
          } catch { /* ignore */ }
          setLoading(false);
          return;
        }
      }
      // Non-OK or non-JSON response — fall through to demo
    } catch {
      // Network error — fall through to demo
    }

    // Fallback to demo data
    if (demo) {
      setClientData(demo);
      setError('Showing local demo data — backend not reachable');
    } else {
      setError('Client not found. Try: client_prab_001, client_gt_lawn_001, client_cb_hvac_001, client_sh_ins_001, client_dm_plumb_001, client_3a1821e2, client_pnb_fin_001');
    }
    setLoading(false);
  };

  const handleElevate = () => {
    setAuthError(null);
    // Simple role elevation — in production this would be Cognito/JWT
    if (authPassword === 'admin2026') {
      setUserRole(ROLES.ADMIN);
    } else if (authPassword === 'super2026') {
      setUserRole(ROLES.SUPERUSER);
    } else {
      setAuthError('Invalid credentials. Access denied.');
    }
    setAuthPassword('');
  };

  const handleDownload = (layer) => {
    // In production: fetch presigned S3 download URL from backend
    // For now: construct the S3 path and open in new tab
    const fileMap = {
      rag_ai: { path: `rag/RAG_AI.pdf`, label: 'Boundary Guardrails Configuration' },
      genai: { path: `genai/GenAI.pdf`, label: 'LLM Configuration' },
      agentic_ai: { path: `agentic/AgenticAI.pdf`, label: 'Comm-AI Configuration' },
    };
    const file = fileMap[layer];
    if (!file) return;

    // Try to fetch a download URL from the backend
    fetch(`/v1/clients/${clientId}/download/${layer}`)
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Not available');
      })
      .then(data => {
        if (data.url) window.open(data.url, '_blank');
        else alert(`Download URL not available for ${file.label}. File path: clients/${clientId}/${file.path}`);
      })
      .catch(() => {
        alert(`${file.label}\nS3 Path: s3://pnp-platform-060768937301/clients/${clientId}/${file.path}\n\nBackend download endpoint not available. Use AWS CLI:\naws s3 cp s3://pnp-platform-060768937301/clients/${clientId}/${file.path} .`);
      });
  };

  const getDemoClient = (id) => {
    const demos = {
      'builder_master_001': { client_id: 'builder_master_001', company_name: 'Empirical-AiS Platform Engine', plan: 'enterprise', tenant_type: 'PARENT', status: 'active', location: 'EC2 Production (3.219.218.53)', contact_email: '[email]', ein: 'XX-XXXXXXX', product_name: 'Comm-AI-Core', product_type: 'Platform-Architect', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-03-25T08:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_prab_001': { client_id: 'client_prab_001', company_name: 'Prab-Restaurant-Hospitality', plan: 'starter', tenant_type: 'INDEPENDENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', website_url: 'https://prab-restaurant.com', contact_phone: '[phone_number]', ein: 'XX-XXXXXXX', product_name: 'GuestInsight-LLM', product_type: 'Hospitality-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-03-28T10:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_gt_lawn_001': { client_id: 'client_gt_lawn_001', company_name: 'GreenThumb-Lawn-Landscape', plan: 'starter', tenant_type: 'INDEPENDENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', website_url: 'https://greenthumb-lawn.com', contact_phone: '[phone_number]', ein: 'XX-XXXXXXX', product_name: 'ServicePro-LLM', product_type: 'Home-Service-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-03-29T10:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_cb_hvac_001': { client_id: 'client_cb_hvac_001', company_name: 'CoolBreeze-HVAC-Services', plan: 'growth', tenant_type: 'INDEPENDENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', website_url: 'https://coolbreeze-hvac.com', contact_phone: '[phone_number]', ein: 'XX-XXXXXXX', product_name: 'ServicePro-LLM', product_type: 'Home-Service-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-03-29T14:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_sh_ins_001': { client_id: 'client_sh_ins_001', company_name: 'SafeHaven-Insurance-Group', plan: 'enterprise', tenant_type: 'PARENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', ein: 'XX-XXXXXXX', product_name: 'RiskGuard-LLM', product_type: 'Fin-Secure-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', sub_clients_count: 4, created_at: '2026-03-30T10:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_sh_ral_001': { client_id: 'client_sh_ral_001', company_name: 'SafeHaven-Insurance-Raleigh', plan: 'starter', tenant_type: 'SUB_CLIENT', status: 'active', location: 'Raleigh, NC', contact_email: '[email]', ein: 'XX-XXXXXXX', product_name: 'RiskGuard-LLM', product_type: 'Fin-Secure-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', parent_client_id: 'client_sh_ins_001', created_at: '2026-03-30T11:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_dm_plumb_001': { client_id: 'client_dm_plumb_001', company_name: 'DrainMaster-Plumbing-Network', plan: 'enterprise', tenant_type: 'PARENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', ein: 'XX-XXXXXXX', product_name: 'ServicePro-LLM', product_type: 'Home-Service-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', sub_clients_count: 6, created_at: '2026-03-30T14:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_kmed_001': { client_id: 'client_kmed_001', company_name: 'Kona-Med-Wellness', plan: 'growth', tenant_type: 'INDEPENDENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', ein: 'XX-XXXXXXX', product_name: 'PatientPortal-LLM', product_type: 'Health-Care-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-04-01T09:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_kauto_001': { client_id: 'client_kauto_001', company_name: 'Kona-Auto-Valet', plan: 'starter', tenant_type: 'SUB_CLIENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', ein: 'XX-XXXXXXX', product_name: 'FleetTracker-LLM', product_type: 'Logistics-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', parent_client_id: 'client_kona_mall_001', created_at: '2026-04-01T10:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_klog_001': { client_id: 'client_klog_001', company_name: 'Kona-Logistics-Smart', plan: 'enterprise', tenant_type: 'INDEPENDENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', ein: 'XX-XXXXXXX', product_name: 'FleetTracker-LLM', product_type: 'Logistics-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-04-01T11:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_kmd_001': { client_id: 'client_kmd_001', company_name: 'KMD-Realty-Inc', plan: 'growth', tenant_type: 'INDEPENDENT', status: 'active', location: '3620 Central Ave #200, Charlotte, NC 28205', contact_email: 'info@kmdrealty.com', website_url: 'https://www.kmdrealty.com', contact_phone: '(704) 493-9818', ein: 'XX-XXXXXXX', product_name: 'AssetManager-LLM', product_type: 'Property-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-04-02T09:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_3a1821e2': { client_id: 'client_3a1821e2', company_name: 'Sports-Avatar-Athletics', plan: 'growth', tenant_type: 'INDEPENDENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', website_url: 'https://sportsavatar.com', contact_phone: '[phone_number]', ein: 'XX-XXXXXXX', product_name: 'AthleteEdge-LLM', product_type: 'Wellness-Intel', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-04-03T10:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
      'client_pnb_fin_001': { client_id: 'client_pnb_fin_001', company_name: 'Prabs-National-Bank', plan: 'enterprise', tenant_type: 'INDEPENDENT', status: 'active', location: '9622 Ardrey Woods Dr, Charlotte, NC 28277', contact_email: '[email]', website_url: 'https://prabsnationalbank.com', contact_phone: '[phone_number]', ein: 'XX-XXXXXXX', product_name: 'BankRisk-LLM', product_type: 'Financial-Bank-Type1', rag_status: 'indexed', genai_status: 'configured', agentic_status: 'configured', created_at: '2026-04-04T09:00:00Z', default_features: ['Comm-AI-Chat', 'LLM-Reporting', 'PDF-Export'] },
    };
    return demos[id] || null;
  };

  const statusDot = (status) => {
    const colors = { indexed: '#4CAF50', configured: '#4CAF50', pending: '#FF9800', error: '#f44336' };
    return <span style={{ color: colors[status] || '#999' }}>● {status || 'unknown'}</span>;
  };

  const roleBadge = () => {
    const colors = { viewer: '#9E9E9E', admin: '#2196F3', superuser: '#9C27B0' };
    return (
      <span style={{
        background: colors[userRole], color: '#fff', padding: '2px 10px',
        borderRadius: '12px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
      }}>{userRole}</span>
    );
  };

  const fieldStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box',
  };

  const btnStyle = (bg) => ({
    padding: '10px 24px', background: bg, color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  });

  const cardStyle = {
    background: '#f9f9f9', padding: '20px', borderRadius: '12px',
    marginBottom: '16px', border: '1px solid #e8e8e8',
  };

  const downloadBtnStyle = (enabled) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', background: enabled ? '#1565C0' : '#ccc',
    color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px',
    fontWeight: 600, cursor: enabled ? 'pointer' : 'not-allowed',
    marginRight: '8px', marginBottom: '8px',
  });

  return (
    <div style={{ padding: '24px', maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <h2 style={{ margin: 0 }}>🔍 View Client Details</h2>
        {roleBadge()}
      </div>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Look up a client by ID to view public details and AI configuration files
      </p>

      {/* Lookup bar */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <input
          type="text" placeholder="Enter client ID (e.g. client_prab_001)"
          value={clientId} onChange={e => setClientId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLookup()}
          style={{ ...fieldStyle, marginBottom: 0, flex: 1 }}
          aria-label="Client ID"
        />
        <button onClick={handleLookup} style={btnStyle('#2196F3')} disabled={loading}>
          {loading ? 'Loading...' : 'View'}
        </button>
      </div>

      {error && <p style={{ color: '#FF9800', fontSize: '13px', marginBottom: '12px' }}>⚠️ {error}</p>}

      {clientData && (
        <>
          {/* Launch Application Button */}
          {clientData.status === 'active' && (
            <div style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', padding: '16px 20px',
              borderRadius: '12px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: '15px' }}>{clientData.company_name}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>{clientData.product_name || 'AI-Powered Operations'} • All 3 AI layers active</div>
              </div>
              <button onClick={() => {
                if (onLaunchApp) onLaunchApp({
                  client_id: clientData.client_id,
                  company_name: clientData.company_name,
                  product_name: clientData.product_name,
                  product_type: clientData.product_type,
                  tenant_type: clientData.tenant_type,
                  plan: clientData.plan,
                  parent_tenant_id: clientData.parent_client_id || null,
                });
              }} style={{
                padding: '10px 20px', background: '#4CAF50', color: '#fff', border: 'none',
                borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                ▶ Launch Application
              </button>
            </div>
          )}

          {/* Public Details — always visible */}
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#1a1a2e' }}>
              📄 Public Information
            </h3>
            <table style={{ width: '100%', fontSize: '14px' }}>
              <tbody>
                {[
                  ['Company Name', clientData.company_name],
                  ['Client ID', <code>{clientData.client_id}</code>],
                  ['Location', clientData.location],
                  ['Contact Email', clientData.contact_email],
                  ['Website', clientData.website_url ? <a href={clientData.website_url} target="_blank" rel="noopener noreferrer" style={{color:'#1565C0'}}>{clientData.website_url}</a> : '—'],
                  ['Phone', clientData.contact_phone || '—'],
                  ['Product / Application', clientData.product_name],
                  ['Plan', clientData.plan],
                  ['Tenant Type', clientData.tenant_type],
                  ['Status', <span style={{ color: clientData.status === 'active' ? '#4CAF50' : '#FF9800' }}>● {clientData.status}</span>],
                  ['Registered', clientData.created_at],
                  ...(clientData.sub_clients_count ? [['Sub-Clients', clientData.sub_clients_count]] : []),
                ].map(([label, value], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px', fontWeight: 600, width: '180px', color: '#555' }}>{label}</td>
                    <td style={{ padding: '8px' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* AI Layer Status — always visible */}
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#1a1a2e' }}>
              🧠 AI Layer Status
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Guardrails', icon: '🛡️', status: clientData.rag_status, desc: 'Boundary Rules' },
                { label: 'LLM', icon: '⚙️', status: clientData.genai_status, desc: 'Operations BI' },
                { label: 'Comm-AI', icon: '🐙', status: clientData.agentic_status, desc: 'Public Agent' },
              ].map(layer => (
                <div key={layer.label} style={{
                  background: '#fff', padding: '14px', borderRadius: '8px',
                  border: '1px solid #e8e8e8', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>{layer.icon}</div>
                  <div style={{ fontWeight: 600, fontSize: '13px' }}>{layer.label}</div>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>{layer.desc}</div>
                  <div style={{ fontSize: '13px' }}>{statusDot(layer.status)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Config Files — requires admin/superuser */}
          <div style={{ ...cardStyle, border: canDownloadFiles ? '1px solid #bbdefb' : '1px solid #e8e8e8' }}>
            <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#1a1a2e' }}>
              📁 AI Configuration Files
            </h3>

            {!canDownloadFiles ? (
              <>
                <p style={{ color: '#888', fontSize: '13px', marginBottom: '12px' }}>
                  🔒 Elevated access required to download AI configuration files.
                  Sign in as Admin or Super User to unlock.
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="password" placeholder="Enter admin or superuser password"
                    value={authPassword} onChange={e => setAuthPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleElevate()}
                    style={{ ...fieldStyle, marginBottom: 0, flex: 1 }}
                    aria-label="Elevation password"
                  />
                  <button onClick={handleElevate} style={btnStyle('#FF9800')}>Elevate</button>
                </div>
                {authError && <p style={{ color: '#f44336', fontSize: '13px', marginTop: '8px' }}>❌ {authError}</p>}
              </>
            ) : (
              <>
                <p style={{ color: '#4CAF50', fontSize: '13px', marginBottom: '12px' }}>
                  ✅ Elevated access granted as {userRole}. You can download AI configuration files.
                </p>
                <div>
                  <button style={downloadBtnStyle(true)} onClick={() => handleDownload('rag_ai')}>
                    🛡️ Download Guardrails
                  </button>
                  <button style={downloadBtnStyle(true)} onClick={() => handleDownload('genai')}>
                    ⚙️ Download LLM
                  </button>
                  <button style={downloadBtnStyle(true)} onClick={() => handleDownload('agentic_ai')}>
                    🐙 Download Comm-AI
                  </button>
                </div>
                <p style={{ color: '#999', fontSize: '11px', marginTop: '8px' }}>
                  Files stored at: s3://pnp-platform-060768937301/clients/{clientData.client_id}/
                </p>
                {userRole === ROLES.SUPERUSER && (
                  <div style={{ marginTop: '12px', padding: '10px', background: '#f3e5f5', borderRadius: '8px' }}>
                    <p style={{ fontSize: '12px', color: '#6A1B9A', margin: 0, fontWeight: 600 }}>
                      🔑 Super User: You also have access to Secrets Manager credentials and tenant DB connection strings.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ViewClient;
